// 串门 (src/visit.ts, office/visits.ts, routes/visits.ts): going to another Raft server's office as myself, tagged
// 「来自 <my server>」, when that server lets mine in; and, for owners and admins, who may come to ours.
// A page is visiting only when its address says so (/?visit=<server id>): then every request it makes for the office
// carries ?visit= too (vq), and the worker treats it as a visit, or refuses it once the visit has ended. Without it the
// page is simply at home: other tabs, old pages and a rolled-back worker never wander into another server's office.
//   设置 → 串门: at home, the offices open to my server (进去 → POST /api/visits/start → /?visit=<id>); visiting, where
//   I am and 回家. Owners and admins: 谁能来串门 (不开放 / 指定的 server / 所有 server) for our office.
//   The top bar, while visiting: 「在 X 串门 · 回家」.
//   邀请串门 (any member): a link (/#vs=<server>&vi=<id>&vt=<tag>; the fragment never reaches a server or a log) that
//   lets people of any other server in, whatever the policy: 有效期 1 小时 / 今天 / 7 天, 人数 1 / 5 / 20 / 不限, 作废.
//   Opening one signed in on another server (takeInvite, from app.js) starts the visit; signed out, it waits in this
//   tab (sessionStorage) for the Raft sign-in.
// Everything shown is set as text (server names come from other servers).
import { icon, label } from '/icons.js';
import { t, has, onLang, fmtDate } from '/i18n.js';

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const q = new URLSearchParams(location.search).get('visit');
/** The server whose office this page visits (from its address), or null: at home. */
export const VISIT = q && UUID.test(q) ? q : null;
/** A same-origin URL for the office, with ?visit= while visiting. */
export const vq = (u) => (VISIT ? `${u}${u.includes('?') ? '&' : '?'}visit=${VISIT}` : u);

const CSS = `
#visitPill{order:1;display:inline-flex;align-items:center;gap:6px;box-sizing:border-box;height:30px;background:#dcfce7;border:2px solid #1b1410;border-radius:10px;padding:2px 4px 2px 8px;font:12px/1.5 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410;max-width:260px}
#visitPill .t{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}
#visitPill button{flex:none;font:inherit;font-weight:600;border:2px solid #1b1410;border-radius:999px;background:#FDD03B;padding:0 8px;cursor:pointer}
#settings .vs-list{margin:6px 0}
#settings .vs-row{display:flex;align-items:center;gap:6px;padding:4px 0;border-top:1px solid #e5d7b8;min-width:0}
#settings .vs-row b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}
#settings .vs-row button,#settings .vs-btn{flex:none;font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:1px 10px;background:#fff;cursor:pointer;display:inline-flex;align-items:center;gap:5px}
#settings .vs-row button{margin-left:auto}
#settings .vs-btn.main{background:#FDD03B;font-weight:600;padding:3px 12px}
#settings .vs-row button:disabled,#settings .vs-btn:disabled{opacity:.5;cursor:default}
#settings .vs-note{font-size:12px;color:#666}
#settings .vs-seg{display:flex;gap:6px;flex-wrap:wrap;margin:4px 0}
#settings .vs-seg button{font:inherit;font-size:12px;border:2px solid #1b1410;border-radius:999px;padding:1px 10px;background:#fff;cursor:pointer}
#settings .vs-seg button.on{background:#FDD03B}
#settings .vs-pick{max-height:180px;overflow:auto;border:2px solid #1b1410;border-radius:8px;background:#fff;padding:2px 8px;margin:4px 0}
#settings .vs-pick label{display:flex;align-items:center;gap:6px;font-size:13px;padding:2px 0;min-width:0}
#settings .vs-pick label span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#settings .vs-err{color:#b91c1c;font-size:12px;min-height:1em;margin:2px 0}`;

/** The words for the office's refusal codes (office/visits.ts, routes/visits.ts): visits.err.<code>; null for one this page doesn't know. */
const WHY = (c) => (c && has(`visits.err.${c}`) ? t(`visits.err.${c}`) : null);
const fmt = (ts) => fmtDate(ts, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
/** 有效期 and 人数's choices: the value sent, its words. */
const TTLS = [['1h', 'visits.ttl.hour'], ['today', 'visits.ttl.today'], ['7d', 'visits.ttl.week']], MAXES = [1, 5, 20, 0];

// ---- an invite link in this page's address (or kept for after the sign-in)
const KEEP = 'deck.visitInvite';
/** The 邀请串门 link this page was opened with (taken out of the address bar), or one kept from before the sign-in. */
export function pendingInvite() {
  const h = new URLSearchParams(location.hash.slice(1));
  let inv = h.get('vi') ? { srv: h.get('vs') ?? '', id: h.get('vi') ?? '', t: h.get('vt') ?? '' } : null;
  if (inv) { history.replaceState(null, '', location.pathname + location.search); try { sessionStorage.setItem(KEEP, JSON.stringify(inv)); } catch {} }
  else try { inv = JSON.parse(sessionStorage.getItem(KEEP) ?? 'null'); } catch {}
  return inv && UUID.test(inv.srv) && /^[A-Za-z0-9_-]{22}$/.test(inv.id) && /^[A-Za-z0-9_-]{22}$/.test(inv.t) ? inv : null;
}
/** Use it (signed in): the visit starts and the page goes there; or why not (a message to show). */
export async function takeInvite(inv) {
  try { sessionStorage.removeItem(KEEP); } catch {}
  if (!confirm(t('visits.join.confirm'))) return t('visits.join.declined');
  const r = await post('/api/visits/join', inv);
  if (r?.ok) { location.replace(`/?visit=${encodeURIComponent(r.server)}`); return null; }
  return WHY(r?.code) ?? t('visits.join.failed');
}
/** 谁能来串门: the policy's values, their words. */
const OPEN = [['off', 'visits.open.off'], ['list', 'visits.open.list'], ['all', 'visits.open.all']];

const post = (path, body = {}) => fetch(path, { method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  .then((r) => r.json().catch(() => ({ ok: false }))).catch(() => null);
const get = (path) => fetch(path, { cache: 'no-store' }).then((r) => r.json().catch(() => ({ ok: false }))).catch(() => null);

/**
 * @param o {addSettingsSection, toast, visit ({server, server_name, from: {id, name}, exp}: this page is visiting; or null),
 *   others() (my other servers in this browser, GET /me: a server I'm signed in to is switched to, not visited),
 *   switchTo(key) (app.js), leave() (stop the socket, voice and mic before the page goes), guest (a 访客: nothing here)}
 * @returns {{goHome(), open()}}
 */
export function initVisits(o) {
  if (o.guest) return { goHome() {}, open() {} };
  if (!document.getElementById('visit-css')) { const s = el('style'); s.id = 'visit-css'; s.textContent = CSS; document.head.append(s); }
  const there = () => (o.visit ? o.visit.server_name || t('visits.otherServer') : '');
  const from = () => o.visit.from?.name || t('visits.yourServer');

  /** 回家: the visit ends there (its office forgets it; the cookie goes), then my own office. */
  async function goHome() {
    await post('/api/visits/end');
    o.leave();
    location.replace('/');
  }
  async function go(s) {
    const mine = o.others().find((x) => x.id === s.id);
    if (mine) { o.switchTo(mine.key); return; } // signed in there already: as a member, not a visitor
    const r = await post('/api/visits/start', { server: s.id });
    if (!r?.ok) { o.toast(WHY(r?.code) ?? t('visits.go.failed')); return; }
    o.leave();
    location.assign(`/?visit=${encodeURIComponent(r.server)}`);
  }

  if (o.visit) { // the top bar: where I am, and the way back
    const pill = el('div'); pill.id = 'visitPill';
    const where = el('span', 't'), b = el('button'); b.type = 'button';
    const draw = () => {
      where.textContent = t('visits.pill', { server: there() }); where.title = t('visits.pill.tip', { server: there(), from: from() });
      b.textContent = t('visits.home'); b.title = t('visits.home.tip');
    };
    draw(); onLang(draw);
    b.onclick = (e) => { e.stopPropagation(); b.disabled = true; void goHome(); };
    pill.append(icon('people'), where, b);
    const put1 = () => document.getElementById('topleft')?.prepend(pill); // first in the top bar (the connection readout is in the corner: app.js #net)
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', put1); else put1();
  }

  o.addSettingsSection({ id: 'visits', title: () => t('visits.title'), icon: 'people', order: 16, render(box) {
    if (o.visit) {
      box.append(el('p', '', t('visits.visiting', { server: there(), from: from() })));
      const b = label(el('button', 'vs-btn main'), 'door', t('visits.home')); b.type = 'button';
      b.onclick = () => { b.disabled = true; void goHome(); };
      box.append(b);
      return;
    }
    box.append(el('p', '', t('visits.intro')));
    const list = el('div', 'vs-list', t('visits.loading')); list.classList.add('vs-note');
    box.append(list);
    void get('/api/visits').then((r) => {
      list.replaceChildren(); list.classList.remove('vs-note');
      if (!r?.ok) { list.append(el('span', 'vs-note', t('visits.listFailed'))); return; }
      if (!r.open.length) { list.append(el('span', 'vs-note', t('visits.none'))); return; }
      for (const s of r.open) {
        const row = el('div', 'vs-row'), n = el('b', '', s.name || 'Raft server'); n.title = s.name || '';
        const signed = o.others().some((x) => x.id === s.id);
        const b = el('button', '', t(signed ? 'visits.switch' : 'visits.enter')); b.type = 'button'; b.dataset.server = s.id;
        b.title = signed ? t('visits.switch.tip', { server: s.name }) : t('visits.enter.tip', { server: s.name });
        b.onclick = () => { b.disabled = true; Promise.resolve(go(s)).finally(() => { b.disabled = false; }); };
        row.append(icon('people'), n, b);
        list.append(row);
      }
    });
    // any member: a link for someone of another server
    const inv = el('div'); box.append(inv); inviteUi(inv);
    // owners and admins: who may come to ours (the server refuses anyone else; then this part stays hidden)
    const adm = el('div'); box.append(adm);
    void get('/api/visits/policy').then((r) => { if (r?.ok) policyUi(adm, r); });
  } });

  /** 邀请串门: make a link (shown once), see the live ones, 作废. */
  function inviteUi(box) {
    let ttl = '1h', max = 1;
    box.append(el('h4', '', t('visits.invite.title')));
    box.append(el('p', 'vs-note', t('visits.invite.note')));
    const seg = (opts, get, set) => {
      const d = el('div', 'vs-seg');
      const draw = () => { for (const b of d.children) b.classList.toggle('on', b.dataset.v === String(get())); };
      for (const [v, text] of opts) { const b = el('button', '', text); b.type = 'button'; b.dataset.v = String(v); b.onclick = () => { set(v); draw(); }; d.append(b); }
      draw(); return d;
    };
    const made = el('div'), err = el('p', 'vs-err'), list = el('div', 'vs-list');
    const mk = label(el('button', 'vs-btn main'), 'people', t('visits.invite.make')); mk.type = 'button'; mk.dataset.act = 'visit-invite';
    mk.onclick = async () => {
      mk.disabled = true; err.textContent = '';
      const end = new Date(); end.setHours(23, 59, 59, 0);
      const j = await post('/api/visits/invites', { ttl, max, ...(ttl === 'today' && { until: end.getTime() }) });
      mk.disabled = false;
      if (!j?.ok) { err.textContent = WHY(j?.code) ?? t('visits.invite.makeFailed'); return; }
      const url = `${location.origin}/#vs=${encodeURIComponent(j.srv)}&vi=${encodeURIComponent(j.id)}&vt=${encodeURIComponent(j.t)}`;
      const inp = el('input'); inp.type = 'text'; inp.readOnly = true; inp.value = url; inp.id = 'visitInviteUrl';
      inp.style.cssText = 'font:inherit;font-size:12px;width:100%;box-sizing:border-box;padding:4px 6px;border:2px solid #1b1410;border-radius:8px';
      const cp = el('button', 'vs-btn', t('visits.invite.copy')); cp.type = 'button';
      cp.onclick = () => { inp.select(); navigator.clipboard?.writeText(url).then(() => o.toast(t('visits.invite.copied')), () => o.toast(t('visits.invite.copyFailed'))); };
      made.replaceChildren(el('p', 'vs-note', t('visits.invite.made', { exp: fmt(j.exp) })), inp, cp);
      void refresh();
    };
    box.append(el('div', 'vs-note', t('visits.ttl')), seg(TTLS.map(([v, k]) => [v, t(k)]), () => ttl, (v) => { ttl = v; }),
      el('div', 'vs-note', t('visits.max')), seg(MAXES.map((n) => [n, n ? t('visits.max.n', { n }) : t('visits.max.any')]), () => max, (v) => { max = v; }), mk, err, made, list);
    async function refresh() {
      const r = await get('/api/visits/invites');
      list.replaceChildren();
      if (!r?.ok || !r.invites.length) return;
      list.append(el('div', 'vs-note', t('visits.invite.live')));
      for (const i of r.invites) {
        const row = el('div', 'vs-row');
        const uses = i.max ? t('visits.uses.max', { uses: i.uses, max: i.max }) : t('visits.uses.n', { uses: i.uses });
        const meta = el('span', 'vs-note', i.here ? t('visits.invite.metaHere', { by: i.by, uses, here: i.here, exp: fmt(i.exp) }) : t('visits.invite.meta', { by: i.by, uses, exp: fmt(i.exp) }));
        meta.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0';
        const x = el('button', '', t('visits.invite.revoke')); x.type = 'button';
        x.onclick = async () => {
          if (!confirm(t('visits.invite.revoke.confirm'))) return;
          x.disabled = true; const j = await post('/api/visits/invites/revoke', { rid: i.rid });
          if (!j?.ok) { x.disabled = false; o.toast(WHY(j?.code) ?? t('visits.invite.revokeFailed')); return; }
          void refresh();
        };
        row.append(meta, x); list.append(row);
      }
    }
    void refresh();
  }

  function policyUi(box, r) {
    let open = r.policy.open; const allow = new Set(r.policy.allow);
    box.append(el('h4', '', t('visits.policy.title')));
    box.append(el('p', 'vs-note', t('visits.policy.note', { max: r.max, here: r.here })));
    const seg = el('div', 'vs-seg'), pick = el('div', 'vs-pick'), err = el('p', 'vs-err');
    const draw = () => {
      for (const b of seg.children) b.classList.toggle('on', b.dataset.v === open);
      pick.style.display = open === 'list' ? '' : 'none';
    };
    for (const [v, k] of OPEN) { const b = el('button', '', t(k)); b.type = 'button'; b.dataset.v = v; b.onclick = () => { open = v; draw(); }; seg.append(b); }
    const known = r.known ?? [];
    // a server on the list Deck has no name for (yet): still shown, so saving keeps it
    for (const id of allow) if (!known.some((k) => k.id === id)) known.push({ id, name: '' });
    pick.append(el('div', 'vs-note', t('visits.policy.listNote')));
    if (!known.length) pick.append(el('span', 'vs-note', t('visits.policy.none')));
    for (const k of known) {
      const l = el('label'), c = el('input'); c.type = 'checkbox'; c.checked = allow.has(k.id);
      c.onchange = () => { if (c.checked) allow.add(k.id); else allow.delete(k.id); };
      const n = el('span', '', k.name || k.id); n.title = k.name ? t('visits.policy.serverTip', { name: k.name, id: k.id }) : k.id;
      l.append(c, n); pick.append(l);
    }
    const save = label(el('button', 'vs-btn main'), 'people', t('visits.policy.save')); save.type = 'button';
    save.onclick = async () => {
      save.disabled = true; err.textContent = '';
      const j = await post('/api/visits/policy', { open, allow: [...allow] });
      save.disabled = false;
      if (!j?.ok) { err.textContent = WHY(j?.code) ?? t('visits.policy.saveFailed'); return; }
      open = j.policy.open; draw();
      o.toast(t(open === 'off' ? 'visits.policy.closed' : 'visits.policy.saved'));
    };
    box.append(seg, pick, save, err);
    draw();
  }

  return { goHome, open: () => o.openSettings?.(false, 'visits') };
}
